import React, { useState } from 'react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { supabase } from '../../supabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { FiX, FiCheck, FiCamera, FiUpload, FiLoader } from 'react-icons/fi';
import { toast } from 'sonner';

const MobileCameraModal = ({ isOpen, onClose }) => {
    const { activeWorkspace } = useWorkspace();
    const [image, setImage] = useState(null);
    const [uploading, setUploading] = useState(false);

    const takePicture = async () => {
        try {
            const photo = await Camera.getPhoto({
                quality: 90,
                allowEditing: false,
                resultType: CameraResultType.Base64,
                source: CameraSource.Prompt // Ask user: Camera or Photos?
            });

            setImage(`data:image/${photo.format};base64,${photo.base64String}`);
        } catch (e) {
            console.error("Camera error:", e);
            onClose(); // Close if cancelled
        }
    };

    const handleUpload = async () => {
        if (!image || !activeWorkspace) return;

        setUploading(true);
        const toastId = toast.loading("Uploading asset...");

        try {
            // Convert Base64 to Blob
            const base64Response = await fetch(image);
            const blob = await base64Response.blob();

            const fileName = `mobile-upload-${Date.now()}.${blob.type.split('/')[1]}`;
            const filePath = `${activeWorkspace.id}/${fileName}`;

            const arrayBuffer = await blob.arrayBuffer();

            const { error: uploadError } = await supabase.storage
                .from('brand-assets')
                .upload(filePath, arrayBuffer, {
                    contentType: blob.type,
                    cacheControl: '3600',
                    upsert: false
                });

            if (uploadError) throw uploadError;

            // Get Public URL
            const { data: { publicUrl } } = supabase.storage
                .from('brand-assets')
                .getPublicUrl(filePath);

            // Save to Brand Assets Table (Optional but good practice)
            // Assuming there is a table for assets, if not, storage is enough for MVP.
            // For now, we'll just notify success.

            toast.success("Asset uploaded successfully!", { id: toastId });
            onClose();
        } catch (e) {
            console.error("Upload failed:", e);
            toast.error("Upload failed. Please try again.", { id: toastId });
        } finally {
            setUploading(false);
            setImage(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-4 animate-in fade-in duration-200">
            {/* Close Button */}
            <button
                onClick={onClose}
                className="max-h-[90vh] flex flex-col absolute top-4 right-4 text-white/50 hover:text-white p-2 rounded-full bg-white/10 backdrop-blur-md"
            >
                <FiX size={24} />
            </button>

            {/* Preview Area */}
            <div className="w-full max-w-sm aspect-[3/4] bg-black rounded-2xl overflow-hidden border border-white/20 relative shadow-2xl mb-8">
                {image ? (
                    <img src={image} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-white/50 space-y-4">
                        <FiCamera size={48} />
                        <p className="text-sm">Tap "Open Camera" below</p>
                    </div>
                )}
            </div>

            {/* Controls */}
            <div className="flex w-full max-w-sm justify-between gap-4">
                {!image ? (
                     <button
                        onClick={takePicture}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-transform active:scale-95 border border-blue-400"
                     >
                        <FiCamera size={20} /> Open Camera
                     </button>
                ) : (
                    <>
                        <button
                            onClick={() => setImage(null)}
                            disabled={uploading}
                            className="flex-1 bg-white/10 hover:bg-white/20 text-white font-medium py-4 rounded-xl backdrop-blur-md transition-colors disabled:opacity-50 border border-white/10"
                        >
                            Retake
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={uploading}
                            className="flex-1 bg-green-500 hover:bg-green-400 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-green-500/20 transition-transform active:scale-95 disabled:opacity-50 border border-green-400"
                        >
                            {uploading ? <FiLoader className="animate-spin" /> : <><FiUpload /> Upload</>}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default MobileCameraModal;
